import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useRouter } from '@tanstack/react-router'
import { ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { errorMessage, isApiError } from '@/lib/api'
import { applyServerErrors } from '@/lib/form-errors'
import { authKeys, meQueryOptions, useLogin, useTwoFactorChallenge } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { safeRedirect } from '../lib'

const route = getRouteApi('/login')

const loginSchema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  remember: z.boolean(),
})
type LoginValues = z.infer<typeof loginSchema>

function useFinishLogin() {
  const qc = useQueryClient()
  const router = useRouter()
  const { redirect } = route.useSearch()
  return async () => {
    await qc.invalidateQueries({ queryKey: authKeys.me() })
    await qc.fetchQuery(meQueryOptions)
    router.history.push(safeRedirect(redirect))
  }
}

export function LoginPage() {
  const [step, setStep] = useState<'credentials' | 'two-factor'>('credentials')
  const { redirect } = route.useSearch()
  return step === 'credentials' ? (
    <AuthLayout
      title="Welcome back"
      description="Sign in to manage your screens, content and team."
      footer={
        <>
          New to Signetix?{' '}
          <Link to="/register" search={{ redirect }} className="font-medium text-primary hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <CredentialsForm onTwoFactor={() => setStep('two-factor')} />
    </AuthLayout>
  ) : (
    <AuthLayout title="Two-factor authentication" description="Your account is protected with two-factor authentication.">
      <TwoFactorForm onBack={() => setStep('credentials')} />
    </AuthLayout>
  )
}

function CredentialsForm({ onTwoFactor }: { onTwoFactor: () => void }) {
  const login = useLogin()
  const finish = useFinishLogin()
  const form = useForm<LoginValues>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '', remember: true } })
  const { errors, isSubmitting } = form.formState

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const result = await login.mutateAsync(values)
      if ('two_factor' in result && result.two_factor) onTwoFactor()
      else await finish()
    } catch (error) {
      if (applyServerErrors(error, form.setError, { fields: ['email', 'password'] })) return
      form.setError('root.server', { message: isApiError(error) && error.status === 429 ? 'Too many attempts. Please wait a minute and try again.' : errorMessage(error) })
    }
  })

  return (
    <form onSubmit={onSubmit} className="grid gap-4" noValidate>
      <FormError message={errors.root?.server?.message} />
      <Field label="Email" error={errors.email?.message}>
        <Input type="email" autoComplete="email" placeholder="you@company.com" autoFocus {...form.register('email')} />
      </Field>
      <Field label="Password" error={errors.password?.message}>
        <Input type="password" autoComplete="current-password" placeholder="••••••••" {...form.register('password')} />
      </Field>
      <div className="flex items-center gap-2">
        <Checkbox id="remember" checked={form.watch('remember')} onCheckedChange={(v) => form.setValue('remember', v === true)} />
        <Label htmlFor="remember" className="font-normal text-muted-foreground">
          Keep me signed in
        </Label>
      </div>
      <Button type="submit" size="lg" variant="brand" loading={isSubmitting} className="mt-2 w-full">
        Sign in
      </Button>
    </form>
  )
}

function TwoFactorForm({ onBack }: { onBack: () => void }) {
  const [mode, setMode] = useState<'code' | 'recovery'>('code')
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const challenge = useTwoFactorChallenge()
  const finish = useFinishLogin()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = value.trim()
    if (!trimmed) return setError(mode === 'code' ? 'Enter the 6-digit code' : 'Enter a recovery code')
    try {
      await challenge.mutateAsync(mode === 'code' ? { code: trimmed.replace(/\s/g, '') } : { recovery_code: trimmed })
      await finish()
    } catch (err) {
      if (isApiError(err)) setError(err.fieldError(mode === 'code' ? 'code' : 'recovery_code') ?? err.detail)
      else setError(errorMessage(err))
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-5" noValidate>
      <div className="flex items-start gap-3 rounded-xl border bg-muted/40 p-4">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {mode === 'code' ? <ShieldCheck className="size-5" /> : <KeyRound className="size-5" />}
        </div>
        <p className="text-sm text-muted-foreground">
          {mode === 'code'
            ? 'Open your authenticator app and enter the 6-digit code for Signetix.'
            : 'Enter one of the emergency recovery codes you saved when enabling 2FA. Each code works once.'}
        </p>
      </div>
      <Field label={mode === 'code' ? 'Authentication code' : 'Recovery code'} error={error ?? undefined}>
        {mode === 'code' ? (
          <Input
            key="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            placeholder="123 456"
            autoFocus
            className="h-12 text-center font-mono text-xl tracking-[0.4em]"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <Input key="recovery" autoComplete="off" placeholder="xxxxx-xxxxx" autoFocus className="font-mono" value={value} onChange={(e) => setValue(e.target.value)} />
        )}
      </Field>
      <Button type="submit" size="lg" variant="brand" loading={challenge.isPending} className="w-full">
        Verify and sign in
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-3.5" /> Back
        </button>
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={() => {
            setMode(mode === 'code' ? 'recovery' : 'code')
            setValue('')
            setError(null)
          }}
        >
          {mode === 'code' ? 'Use a recovery code' : 'Use authenticator code'}
        </button>
      </div>
    </form>
  )
}
